/**
 * Shared-facility redemption candidates (PROD incident 2026-10-24, Apache Rifle Works / facility 16816).
 *
 * A facility voucher names facility IDs, not a store. When the same facility ID sits on more than one
 * organization (an organization row, or a location of a different organization), redeem_voucher_guarded cannot
 * tell which organization is redeeming and returns `context_required`. The /redeem page then shows this list
 * so the partner picks the store by name, city and state, and re-submits with that organization/location.
 *
 * TRUST: this list is UX only. The server (redeem_voucher_guarded) re-checks whatever the client sends against
 * the voucher's facility mapping and refuses anything outside it (`wrong_scope`).
 *
 * The entry rule mirrors the guard's accepted set and is valid under BOTH guard bodies:
 *   - legacy guard (PROD today): a location is accepted when its own facility ID OR its parent's is in the
 *     voucher's set; an organization when its own facility ID or any of its locations' is.
 *   - R2 guard (map-listed redemption, QA): `redemption_policy_entries` for facility_v1 -- the same formula as below.
 * Entries:
 *   direct    a live location (location and parent not archived) whose own facility ID is in the set
 *   expanded  every live location of a live organization whose own facility ID is in the set
 *   org_only  such an organization that has no live locations (the organization itself)
 * Ambiguity (whether a picker is needed) uses the guard's own predicate: the DISTINCT organizations over ALL rows
 * holding the facility ID, archived or not (organization rows + locations' parent organizations).
 */

export interface FacilityLocationRow {
  id: string;
  name: string;
  organization_id: string;
  facility_id: string | null;
  is_archived: boolean | null;
  address_line1?: string | null;
  city?: string | null;
  state?: string | null;
}

export interface FacilityOrganizationRow {
  id: string;
  name: string;
  facility_id?: string | null;
  is_archived: boolean | null;
  address_line1?: string | null;
  address_city?: string | null;
  address_state?: string | null;
}

export interface FacilityRedemptionCandidate {
  /** Unique entry id: the location id for a store, the organization id for an organization without stores. */
  id: string;
  name: string;
  organization_id: string;
  location_id?: string;
  organization_name?: string;
  address?: string;
  city?: string;
  state?: string;
}

export interface FacilityRedemptionCandidates {
  /** Distinct organizations holding the facility ID(s), archived rows included (the guard's ambiguity test). */
  organizationIds: string[];
  /** Live entries the partner can pick, sorted by state, city, name. */
  candidates: FacilityRedemptionCandidate[];
}

const clean = (v: string | null | undefined): string | undefined => {
  const t = (v ?? "").trim();
  return t ? t : undefined;
};

/** Normalized facility-ID set (trimmed, blanks dropped). */
export function normalizeFacilityIds(ids: unknown): string[] {
  if (!Array.isArray(ids)) {
    return [];
  }
  const out = new Set<string>();
  for (const x of ids) {
    if (typeof x === "string" && x.trim()) {
      out.add(x.trim());
    }
  }
  return [...out];
}

/**
 * Pure: build the candidate list from rows already read.
 * @param facilityIds        the voucher's facility IDs
 * @param facilityLocations  locations whose facility_id is one of them (any archived state)
 * @param facilityOrgs       organizations whose facility_id is one of them (any archived state)
 * @param parentOrgs         the parent organizations of facilityLocations (for names + archived state)
 * @param childLocations     ALL locations of the organizations in facilityOrgs (any archived state)
 */
export function buildFacilityRedemptionCandidates(
  facilityIds: string[],
  facilityLocations: FacilityLocationRow[],
  facilityOrgs: FacilityOrganizationRow[],
  parentOrgs: FacilityOrganizationRow[],
  childLocations: FacilityLocationRow[],
): FacilityRedemptionCandidates {
  const fac = new Set(normalizeFacilityIds(facilityIds));
  const inSet = (v: string | null | undefined) => {
    const t = clean(v);
    return t !== undefined && fac.has(t);
  };
  const locs = facilityLocations.filter((l) => inSet(l.facility_id));
  const orgs = facilityOrgs.filter((o) => inSet(o.facility_id));

  const organizationIds = [
    ...new Set([
      ...orgs.map((o) => o.id),
      ...locs.map((l) => l.organization_id).filter(Boolean),
    ]),
  ];

  const orgById = new Map<string, FacilityOrganizationRow>();
  for (const o of [...parentOrgs, ...orgs]) {
    orgById.set(o.id, o);
  }
  const live = (b: boolean | null | undefined) => !b;

  const entries = new Map<string, FacilityRedemptionCandidate>();
  const addLocation = (l: FacilityLocationRow) => {
    const parent = orgById.get(l.organization_id);
    if (!parent || !live(parent.is_archived) || !live(l.is_archived)) {
      return;
    }
    entries.set(l.id, {
      id: l.id,
      name: l.name,
      organization_id: l.organization_id,
      location_id: l.id,
      organization_name: parent.name,
      address: clean(l.address_line1),
      city: clean(l.city),
      state: clean(l.state),
    });
  };

  // direct: a live location whose own facility ID is in the set
  locs.forEach(addLocation);
  // expanded / org_only: a live matching organization is its live locations, or itself when it has none
  for (const o of orgs) {
    if (!live(o.is_archived)) {
      continue;
    }
    const liveChildren = childLocations.filter(
      (l) => l.organization_id === o.id && live(l.is_archived),
    );
    if (liveChildren.length > 0) {
      liveChildren.forEach(addLocation);
    } else {
      entries.set(o.id, {
        id: o.id,
        name: o.name,
        organization_id: o.id,
        organization_name: o.name,
        address: clean(o.address_line1),
        city: clean(o.address_city),
        state: clean(o.address_state),
      });
    }
  }

  const candidates = [...entries.values()].sort(
    (a, b) =>
      (a.state ?? "").localeCompare(b.state ?? "") ||
      (a.city ?? "").localeCompare(b.city ?? "") ||
      a.name.localeCompare(b.name),
  );
  return { organizationIds, candidates };
}

/** One line for the picker: "City, ST" (either part may be missing). */
export function candidatePlace(c: FacilityRedemptionCandidate): string {
  return [c.city, c.state].filter(Boolean).join(", ");
}
